import { ref, computed, type Ref } from 'vue'
import {
  ATTACHMENT_LIMIT_PER_FOLDER,
  MAX_VISIBLE_ATTACHMENTS,
  ATTACHMENT_LIMIT_TOOLTIP_TEXT,
  ATTACHMENT_LIMIT_ARIA_DESCRIPTION,
} from '../attachment_constants'
import type { LibraryContentObject } from '../library_types'

export function useAttachments(attachments: Ref<LibraryContentObject[]>) {
  const showAllAttachments = ref(false)
  const openAttachmentModal = ref(false)
  const attachmentToEdit = ref<LibraryContentObject | null>(null)

  const showAttachmentToast = ref(false)
  const attachmentToastMessage = ref('')
  const attachmentToastError = ref(false)
  const attachmentToastTimeout = 3000

  const currentFolderAttachmentCount = computed(() => {
    return attachments.value?.length || 0
  })

  const attachmentLimitReached = computed(() => {
    return currentFolderAttachmentCount.value >= ATTACHMENT_LIMIT_PER_FOLDER
  })

  const hasMoreAttachments = computed(() => {
    return currentFolderAttachmentCount.value > MAX_VISIBLE_ATTACHMENTS
  })

  const visibleAttachments = computed(() => {
    const sortedAttachments = [...(attachments.value || [])].sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at || 0).getTime()
      const dateB = new Date(b.updated_at || b.created_at || 0).getTime()
      return dateB - dateA // Most recent first
    })
    
    if (showAllAttachments.value) return sortedAttachments
    return sortedAttachments.slice(0, MAX_VISIBLE_ATTACHMENTS)
  })

  function showAttachmentToastMessage(message: string, isError: boolean = false): void {
    attachmentToastMessage.value = message
    attachmentToastError.value = isError
    showAttachmentToast.value = true
    setTimeout(() => {
      requestAnimationFrame(() => {
        showAttachmentToast.value = false
      })
    }, attachmentToastTimeout)
  }

  function handleAttachmentEdit(attachment: LibraryContentObject): void {
    attachmentToEdit.value = attachment
    openAttachmentModal.value = true
  }

  function toggleAttachmentsView(): void {
    showAllAttachments.value = !showAllAttachments.value
  }

  function openAddAttachmentModal(): void {
    if (attachmentLimitReached.value) {
      return
    }
    attachmentToEdit.value = null
    openAttachmentModal.value = true
  }

  return {
    showAllAttachments,
    openAttachmentModal,
    attachmentToEdit,
    showAttachmentToast,
    attachmentToastMessage,
    attachmentToastError,
    currentFolderAttachmentCount,
    attachmentLimitReached,
    hasMoreAttachments,
    visibleAttachments,
    showAttachmentToastMessage,
    handleAttachmentEdit,
    toggleAttachmentsView,
    openAddAttachmentModal,
    ATTACHMENT_LIMIT_TOOLTIP_TEXT,
    ATTACHMENT_LIMIT_ARIA_DESCRIPTION,
  }
}
